import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { http } from "../../shared/api/http.js";
import { weddingKeys } from "../weddings/weddings.api.js";
import type { EventRow, MediaItem, WeddingRow } from "../../shared/types.js";
import type { ThemeId } from "@smartvow/shared";

export interface EventDetail {
  event: EventRow;
  wedding: WeddingRow;
}
interface GalleryResp {
  items: MediaItem[];
  nextCursor: string | null;
}

export const eventKeys = {
  detail: (id: string) => ["event", id] as const,
  qr: (id: string) => ["event-qr", id] as const,
  gallery: (id: string) => ["gallery", "owner", id] as const,
  guests: (id: string) => ["event-guests", id] as const,
};

export interface EventGuest {
  email: string | null;
  name: string;
  total: number;
  images: number;
  videos: number;
  ready: number;
  registered: boolean;
  registeredAt: string | null;
  firstAt: string | null;
  lastAt: string | null;
}

export const useEventGuests = (id: string) =>
  useQuery({
    queryKey: eventKeys.guests(id),
    refetchInterval: 20_000,
    queryFn: () => http.get<{ items: EventGuest[] }>(`/events/${id}/guests`),
  });

export const fetchEventDetail = (id: string) => http.get<EventDetail>(`/events/${id}`);

export const useEvent = (id: string) =>
  useQuery({ queryKey: eventKeys.detail(id), queryFn: () => fetchEventDetail(id) });

export const useEventQr = (id: string) =>
  useQuery({
    queryKey: eventKeys.qr(id),
    queryFn: () =>
      http.get<{ guestUrl: string; showUrl: string; qrDataUrl: string; token: string }>(
        `/events/${id}/qr`,
      ),
  });

export function useRegenerateQr(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => http.post(`/events/${id}/regenerate-qr`),
    onSuccess: () => qc.invalidateQueries({ queryKey: eventKeys.qr(id) }),
  });
}

export function useUpdateEventTheme(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (theme: ThemeId) => http.patch(`/events/${id}`, { theme }),
    onSuccess: () => qc.invalidateQueries({ queryKey: eventKeys.detail(id) }),
  });
}

export interface EventPatch {
  name?: string;
  date?: string;
  location?: string;
  captureStartAt?: string;
  captureEndAt?: string;
  revealAt?: string;
  wallMessage?: string;
  wallEnabled?: boolean;
}

export function useUpdateEvent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: EventPatch) => http.patch(`/events/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: eventKeys.detail(id) }),
  });
}

export function useDeleteEvent(id: string, weddingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => http.del(`/events/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: weddingKeys.detail(weddingId) }),
  });
}

/** Galerie côté propriétaire : tous les statuts, toutes les pages, auto-refresh tant que ça traite. */
export function useOwnerGallery(id: string) {
  return useInfiniteQuery({
    queryKey: eventKeys.gallery(id),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      http.get<GalleryResp>(
        `/events/${id}/gallery${pageParam ? `?cursor=${encodeURIComponent(pageParam)}` : ""}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: (q) =>
      (q.state.data?.pages ?? []).some((p) =>
        p.items.some((m) => m.status === "processing" || m.status === "uploading"),
      )
        ? 5000
        : false,
  });
}

export function useDeleteMedia(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mediaId: string) => http.del(`/media/${mediaId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: eventKeys.gallery(eventId) }),
  });
}
